import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, MessageSquarePlus } from 'lucide-react';

import { assistantsService } from '../../services/api/assistants';
import { settingsService } from '../../services/api/settings';

interface Props {
  projetId: number;
  onErreur: (titre: string, message: string) => void;
}

interface Assistant {
  id: number;
  title: string;
  mode?: string;
  category_name?: string;
}

/**
 * Ouverture d'un échange depuis un projet.
 *
 * Sans cela, il fallait partir d'un assistant puis y rattacher l'activité
 * après coup — l'ordre inverse de la façon dont le travail se pense : on
 * traite un dossier, et l'on choisit l'outil qu'il appelle.
 *
 * L'échange ouvert ici est rattaché au projet dès sa création, donc il hérite
 * aussitôt des documents déposés dans le dossier.
 */
const DemarrerEchange: React.FC<Props> = ({ projetId, onErreur }) => {
  const navigate = useNavigate();
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [choix, setChoix] = useState('');
  const [ouverture, setOuverture] = useState(false);

  useEffect(() => {
    settingsService
      .getUseCases({ is_active: true })
      .then((liste: Assistant[]) =>
        // Seuls les assistants intégrés ouvrent une conversation ; ceux qui
        // sont restés de simples liens n'ont rien à rattacher.
        setAssistants(liste.filter((a) => a.mode !== 'lien')),
      )
      .catch(() => onErreur('Chargement impossible', "La liste des assistants n'a pas pu être chargée."));
  }, [onErreur]);

  const demarrer = async () => {
    if (!choix) return;
    setOuverture(true);
    try {
      const conversation = await assistantsService.ouvrirConversation(Number(choix), projetId);
      navigate(`/workspace/conversation/${conversation.id}`);
    } catch (erreur: any) {
      onErreur("Ouverture impossible", erreur.message);
      setOuverture(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      <select
        value={choix}
        onChange={(e) => setChoix(e.target.value)}
        disabled={ouverture || assistants.length === 0}
        className="min-w-0 flex-1 rounded-xl border-0 bg-eg-papier px-3.5 py-2 text-[13px] text-eg-encre
          focus:border-eg-accent focus:outline-none focus:ring-1 focus:ring-eg-accent
          disabled:opacity-50"
      >
        <option value="">Choisir un assistant...</option>
        {assistants.map((a) => (
          <option key={a.id} value={a.id}>
            {a.title}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={demarrer}
        disabled={ouverture || !choix}
        className="inline-flex shrink-0 items-center gap-2 rounded-full bg-eg-ombre px-4 py-2 text-[13px] hover:bg-eg-profond
          font-medium text-eg-blanc transition-colors
          disabled:opacity-50"
      >
        {ouverture ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <MessageSquarePlus className="h-4 w-4" />
        )}
        Démarrer un échange
      </button>
    </div>
  );
};

export default DemarrerEchange;
